import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { OfferApiService, ReturnStatus, AudienceRecord } from '@app/areas/offers/services/offer.api.service';
import { catchError, interval, of, Subject, switchMap, takeUntil } from 'rxjs';
import { SignalRService } from '@app/resources/services/signalr.service';

export interface ActivityFeedUpdateEvent {
  offerId: number;
  consumerId: number;
  returnStatus: ReturnStatus;
}

@Component({
  selector: 'app-offer-activity-feed',
  templateUrl: './offer-activity-feed.component.html',
  styleUrls: ['./offer-activity-feed.component.scss'],
  standalone: true,
  imports: [NgIf, TableModule, SkeletonModule, TagModule],
})
export class OfferActivityFeedComponent implements OnInit, OnDestroy {
  private readonly offerApiService = inject(OfferApiService);
  private readonly signalRService = inject(SignalRService);
  private readonly destroy$ = new Subject<void>();

  @Input() offerId!: number;

  records: AudienceRecord[] = [];
  isLoading = true;
  isError = false;

  ngOnInit(): void {
    if (!this.offerId) return;

    // Load initial data
    this.offerApiService
      .getOfferActivityFeed(this.offerId)
      .pipe(
        catchError(() => {
          this.isError = true;
          this.isLoading = false;
          return of([]);
        })
      )
      .subscribe((records) => {
        this.records = records;
        this.isLoading = false;
      });

    // Subscribe to SignalR for real-time updates
    if (this.signalRService.hasConnectionStarted()) {
      this.signalRService.hubConnection.on('OfferActivityFeedUpdated', (event: ActivityFeedUpdateEvent) => {
        if (event.offerId !== this.offerId) return;
        this.records = this.records.map((r) =>
          r.consumerId === event.consumerId ? { ...r, returnStatus: event.returnStatus } : r
        );
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.signalRService.hasConnectionStarted()) {
      this.signalRService.hubConnection.off('OfferActivityFeedUpdated');
    }
  }

  getStatusSeverity(status: ReturnStatus): 'success' | 'warning' | 'danger' | 'info' | undefined {
    switch (status) {
      case ReturnStatus.Returned:
        return 'success';
      case ReturnStatus.Pending:
        return 'warning';
      case ReturnStatus.NoReturn:
        return undefined;
    }
  }

  getStatusLabel(status: ReturnStatus): string {
    switch (status) {
      case ReturnStatus.Returned:
        return 'Used';
      case ReturnStatus.Pending:
        return 'Not Used Yet';
      case ReturnStatus.NoReturn:
        return 'No Return';
    }
  }

  getStatusIcon(status: ReturnStatus): string {
    switch (status) {
      case ReturnStatus.Returned:
        return 'pi pi-check';
      case ReturnStatus.Pending:
        return 'pi pi-clock';
      case ReturnStatus.NoReturn:
        return 'pi pi-times';
    }
  }
}
